import { quantileSorted } from 'd3-array';
import { explain, type Ledger, type LedgerColumn } from './core/ledger';
import { type SimulateOptions, simulatePath } from './core/simulate';
import type { Plan } from './plan/model';
import type { Rng } from './sampling/rng';
import type { ReturnData, Sampler } from './sampling/sampler';

/**
 * Results layer (PLAN.md §2c). Aggregates many path ledgers into the user-facing numbers:
 * per-year percentile bands, terminal-wealth distribution, and success probability. Every
 * aggregate here is derived FROM the ledgers (ledger-first invariant) and the individual
 * path ledgers are kept for drill-down. The math is elementwise over `Float64Array`
 * columns; percentiles come from `d3-array`.
 */

/** The percentile bands plotted as a fan chart. */
export const DEFAULT_PERCENTILES = [0.05, 0.25, 0.5, 0.75, 0.95] as const;

export interface PercentileBand {
  /** The percentile in [0, 1]. */
  readonly p: number;
  /** One value per simulated year (length = horizon). */
  readonly perYear: number[];
}

export interface ResultSet {
  /** Every path's ledger, retained for drill-down (the debuggability promise). */
  readonly ledgers: readonly Ledger[];
  /** Per-year percentile bands of end balance. */
  readonly bands: PercentileBand[];
  /**
   * SUCCESS PROBABILITY = the fraction of paths that never run out of money: a path SUCCEEDS
   * iff its `endBalance` is non-negative in EVERY simulated year (it FAILS the first year the
   * balance goes negative). For a 4%-rule Trinity backtest this is the headline "% of
   * retirements that survived". 0 when there are no paths. See {@link successProbability}.
   */
  readonly successProbability: number;
  /** Terminal (final-year) end balance for each path, sorted ascending. */
  readonly terminalWealth: number[];
  /**
   * M3 (additive, OPTIONAL so the M2 `runMonteCarlo` shape stays valid). Summary of the
   * terminal-wealth distribution (min / percentiles / median / max / mean), computed from
   * `terminalWealth`. Build agents populate it; consumers that predate it ignore it.
   */
  readonly terminalWealthSummary?: DistributionSummary;
  /**
   * M3 (additive, OPTIONAL). The percentiles requested for `bands` (echoes
   * `options.percentiles ?? DEFAULT_PERCENTILES`), so a consumer can label fan-chart bands
   * without re-deriving them.
   */
  readonly percentiles?: readonly number[];
}

/**
 * Five-number-ish summary of a distribution (terminal wealth, by default). All in plan
 * currency. `percentiles` mirrors `bands` percentiles applied to the terminal values.
 */
export interface DistributionSummary {
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly median: number;
  /** Value at each requested percentile, parallel to {@link DistributionSummary.percentiles}. */
  readonly atPercentiles: readonly number[];
  /** The percentiles `atPercentiles` were read at (e.g. `[0.05,0.25,0.5,0.75,0.95]`). */
  readonly percentiles: readonly number[];
}

/**
 * Compute percentile bands of a ledger column across paths, year by year. For each year we
 * gather that column's value from every ledger, sort once, and read each requested quantile.
 */
export function percentileBands(
  ledgers: readonly Ledger[],
  column: LedgerColumn,
  percentiles: readonly number[] = DEFAULT_PERCENTILES,
): PercentileBand[] {
  const bands: PercentileBand[] = percentiles.map((p) => ({ p, perYear: [] }));
  if (ledgers.length === 0) {
    return bands;
  }
  const horizon = ledgers[0]?.length ?? 0;
  const scratch = new Float64Array(ledgers.length);

  for (let y = 0; y < horizon; y++) {
    for (let k = 0; k < ledgers.length; k++) {
      scratch[k] = (ledgers[k] as Ledger).get(y, column);
    }
    const sorted = Array.from(scratch).sort((a, b) => a - b);
    for (let b = 0; b < bands.length; b++) {
      const band = bands[b] as PercentileBand;
      band.perYear.push(quantileSorted(sorted, band.p) ?? Number.NaN);
    }
  }
  return bands;
}

/**
 * Success probability = fraction of paths that never run out of money. A path "fails" the
 * first year it cannot fund its outflows.
 *
 * ENRICHMENT (floor-at-zero): the portfolio is now FLOORED at 0 rather than allowed to go
 * negative, so a failed path's `endBalance` is exactly 0, not negative. Failure is therefore
 * detected via the `depleted` ledger flag (1 in the first year the year could not be funded),
 * with the legacy `endBalance < 0` kept as a defensive belt-and-suspenders for any caller that
 * builds a ledger by hand. A path FAILS if it is depleted in ANY year (or, defensively, goes
 * negative). Back-compat: a fully-funded path has `depleted === 0` every year and a non-negative
 * balance, so it still counts as a success — the Trinity ~94% gate is unchanged.
 */
export function successProbability(ledgers: readonly Ledger[]): number {
  if (ledgers.length === 0) {
    return 0;
  }
  let succeeded = 0;
  for (const ledger of ledgers) {
    let ok = true;
    const hasDepleted = ledger.hasColumn('depleted');
    for (let y = 0; y < ledger.length; y++) {
      const failed =
        (hasDepleted && ledger.get(y, 'depleted') > 0.5) || ledger.get(y, 'endBalance') < 0;
      if (failed) {
        ok = false;
        break;
      }
    }
    if (ok) {
      succeeded++;
    }
  }
  return succeeded / ledgers.length;
}

/** Final-year end balance for each path, sorted ascending. */
export function terminalWealth(ledgers: readonly Ledger[]): number[] {
  const out: number[] = [];
  for (const ledger of ledgers) {
    if (ledger.length > 0) {
      out.push(ledger.get(ledger.length - 1, 'endBalance'));
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * M3 (BUILD agent fills the body). Summarise a SORTED-ASCENDING value array (e.g. the output of
 * {@link terminalWealth}) into a {@link DistributionSummary}: min/max/mean/median plus the value
 * at each requested percentile (via `d3-array` `quantileSorted` on the already-sorted input).
 * Empty input -> all-zero summary. Used to fill `ResultSet.terminalWealthSummary`.
 */
export function summariseDistribution(
  sortedValues: readonly number[],
  percentiles: readonly number[] = DEFAULT_PERCENTILES,
): DistributionSummary {
  if (sortedValues.length === 0) {
    return {
      min: 0,
      max: 0,
      mean: 0,
      median: 0,
      atPercentiles: percentiles.map(() => 0),
      percentiles: [...percentiles],
    };
  }
  // `sortedValues` is required ascending (e.g. the output of `terminalWealth`); min/max are the
  // ends, the mean is the arithmetic average, and every quantile reads off the sorted array via
  // `d3-array`'s `quantileSorted` (R-7 method) so it matches the percentile bands' convention.
  const n = sortedValues.length;
  const min = sortedValues[0] as number;
  const max = sortedValues[n - 1] as number;
  let total = 0;
  for (const v of sortedValues) {
    total += v;
  }
  const mean = total / n;
  const median = quantileSorted(sortedValues as number[], 0.5) ?? Number.NaN;
  const atPercentiles = percentiles.map(
    (p) => quantileSorted(sortedValues as number[], p) ?? Number.NaN,
  );
  return { min, max, mean, median, atPercentiles, percentiles: [...percentiles] };
}

/**
 * M3 PER-PATH DRILL-DOWN (the ledger-first promise surfaced on a ResultSet). Returns the
 * bank-statement `explain(year)` rendering of ONE path's ledger — the same `explain` the core
 * exposes, indexed into the chosen path. `pathIndex` selects a path (0-based, into
 * `result.ledgers`); `period` is a calendar year matched against that ledger's `period` column.
 * This guarantees any aggregate (a percentile band, a success/fail) can be traced back to a
 * concrete year of a concrete simulated path. (BUILD agent fills the body — it is a thin
 * `explain(result.ledgers[pathIndex], period)` with bounds handling.)
 */
export function explainPath(result: ResultSet, pathIndex: number, period: number): string {
  const ledger = result.ledgers[pathIndex];
  if (ledger === undefined) {
    return `No path ${pathIndex} in result (have ${result.ledgers.length}).`;
  }
  return explain(ledger, period);
}

export interface RunMonteCarloOptions extends SimulateOptions {
  /** Historical return data the sampler draws from. */
  readonly data: ReturnData;
  /** Seeded PRNG — owns all randomness so the run is reproducible. */
  readonly rng: Rng;
  /** Percentiles for the fan-chart bands. */
  readonly percentiles?: readonly number[];
}

/**
 * Run a Monte Carlo: the sampler produces N return sequences from the seeded `rng`, the
 * deterministic core runs once per sequence, and we aggregate the resulting ledgers.
 * Reproducible: same plan + same `data` + same seeded `rng` -> identical ResultSet.
 */
export function runMonteCarlo(
  plan: Plan,
  sampler: Sampler,
  n: number,
  options: RunMonteCarloOptions,
): ResultSet {
  // The sampler owns ALL randomness: it draws N ReturnInputs from the seeded `rng`. The pure,
  // seed-free `simulatePath` then runs once per drawn input, so the whole run is a deterministic
  // function of (plan, data, seed) — same seed -> identical ResultSet. Note `HistoricalSequenceSampler`
  // enumerates rolling windows, so the produced path count may differ from `n` (documented).
  const sequences = sampler.sample(options.data, plan.horizonYears, n, options.rng);
  // Thread both M2 SimulateOptions (taxPolicy + ordering) through to the core, omitting absent
  // keys so the M2 defaults (NoTaxPolicy / DefaultOrdering) still apply when unset.
  const simulateOptions: SimulateOptions = {
    ...(options.taxPolicy ? { taxPolicy: options.taxPolicy } : {}),
    ...(options.ordering ? { ordering: options.ordering } : {}),
  };
  const ledgers: Ledger[] = sequences.map((seq) => simulatePath(plan, seq, simulateOptions));

  const percentiles = options.percentiles ?? DEFAULT_PERCENTILES;
  const sortedTerminal = terminalWealth(ledgers);
  return {
    ledgers,
    bands: percentileBands(ledgers, 'endBalance', percentiles),
    successProbability: successProbability(ledgers),
    terminalWealth: sortedTerminal,
    // M3 additive fields: a summary of the terminal-wealth distribution and an echo of the band
    // percentiles, so a consumer can label fan-chart bands without re-deriving them.
    terminalWealthSummary: summariseDistribution(sortedTerminal, percentiles),
    percentiles: [...percentiles],
  };
}
